import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CountUp from "react-countup";
import {
    alpha,
    Alert,
    AlertTitle,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import ReportOutlinedIcon from "@mui/icons-material/ReportOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import GradingOutlinedIcon from "@mui/icons-material/GradeOutlined";
import AddIcon from "@mui/icons-material/Add";
import { boardingModeLabel, schoolOffersBoarding } from "../../utils/boardingUtils";
import {
    Area,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import SeeNotice from "../../components/SeeNotice";
import RecentActivityPanel from "../../components/RecentActivityPanel";
import { AdminSetupChecklist } from "../../components/admin/AdminPageGuide";
import CurrentTermIndicator from "../../components/admin/CurrentTermIndicator";
import SubscriptionStatusBanner from "../../components/subscription/SubscriptionStatusBanner";
import { allClassesHaveHomeroom, getSetupChecklist } from "../../config/adminWorkflow";
import { fetchTermConfigurationStatus, inferClassEducationLevel } from "../../utils/currentTermUtils";
import { getApiBaseUrl } from "../../config/api";
import { getAllSclasses } from "../../redux/sclassRelated/sclassHandle";
import { getAllStudents } from "../../redux/studentRelated/studentHandle";
import { getAllTeachers } from "../../redux/teacherRelated/teacherHandle";
import { getFeeSummary, getAllFeeStructures, getPendingFeeSubmissions } from "../../redux/feeRelated/feeHandle";
import PendingFeePaymentAlert from "../../components/finance/PendingFeePaymentAlert";
import { useDashboardFeatures } from "../../hooks/useDashboardFeatures";

const HERO_ACTIONS = [
    {
        to: "/Admin/students/chooseclass",
        label: "Step 2: Add student",
        variant: "contained",
    },
    {
        to: "/Admin/addclass",
        label: "Step 1: Add class",
        variant: "outlined",
    },
];

const ATTENDANCE_OVERVIEW = [
    { day: "Mon", present: 94, absent: 6 },
    { day: "Tue", present: 91, absent: 9 },
    { day: "Wed", present: 96, absent: 4 },
    { day: "Thu", present: 89, absent: 11 },
    { day: "Fri", present: 93, absent: 7 },
];

const PERFORMANCE_TREND = [
    { month: "Jan", score: 72 },
    { month: "Feb", score: 76 },
    { month: "Mar", score: 74 },
    { month: "Apr", score: 81 },
    { month: "May", score: 84 },
    { month: "Jun", score: 88 },
];

const EXAM_RESULTS_SUMMARY = [
    { subject: "Mathematics", avg: 82, passRate: 91 },
    { subject: "English", avg: 76, passRate: 86 },
    { subject: "Science", avg: 79, passRate: 88 },
    { subject: "Social Studies", avg: 73, passRate: 82 },
];

const SHORTCUTS = [
    { to: "/Admin/students", label: "Students by class", sub: "Open a class roster", Icon: PeopleAltOutlinedIcon, color: "primary" },
    { to: "/Admin/students/chooseclass", label: "Add student", sub: "Pick class, then enrol", Icon: PersonAddAlt1OutlinedIcon, color: "primary" },
    { to: "/Admin/teachers", label: "Teachers", sub: "Staff & HR", Icon: SupervisorAccountOutlinedIcon, color: "secondary" },
    { to: "/Admin/classes", label: "Classes", sub: "Rooms & capacity", Icon: ClassOutlinedIcon, color: "secondary" },
    { to: "/Admin/addclass", label: "Add class", sub: "New cohort", Icon: PostAddOutlinedIcon, color: "info" },
    { to: "/Admin/subjects/chooseclass", label: "Subjects", sub: "Curriculum", Icon: AssignmentOutlinedIcon, color: "info" },
    { to: "/Admin/notices", label: "Notices", sub: "Announcements", Icon: AnnouncementOutlinedIcon, color: "success" },
    { to: "/Admin/addnotice", label: "Post notice", sub: "Broadcast", Icon: BoltOutlinedIcon, color: "success" },
    { to: "/Admin/complains", label: "Complaints", sub: "Inbox", Icon: ReportOutlinedIcon, color: "warning" },
    { to: "/Admin/exams/review", label: "Marks review", sub: "Exams module", Icon: FactCheckOutlinedIcon, color: "info" },
    { to: "/Admin/attendance/review", label: "Attendance review", sub: "Attendance module", Icon: EventAvailableOutlinedIcon, color: "secondary" },
    { to: "/Admin/staff/attendance", label: "Staff sign-in", sub: "Arrival & departure", Icon: AccessTimeOutlinedIcon, color: "secondary" },
];

const QUICK_LINKS = [
    { to: "/Admin/staff/attendance", label: "Staff sign-in book", sub: "Arrival & departure today", Icon: AccessTimeOutlinedIcon, color: "secondary" },
    { to: "/Admin/students/chooseclass", label: "Add Student", sub: "Pick class, then enrol", Icon: PersonAddAlt1OutlinedIcon, color: "primary" },
    { to: "/Admin/addclass", label: "Add Class", sub: "New cohort", Icon: PostAddOutlinedIcon, color: "secondary" },
    { to: "/Admin/classes/results/overview", label: "View Results", sub: "Marks by class", Icon: GradingOutlinedIcon, color: "info" },
    { to: "/Admin/fees/structures", label: "Fee Structures", sub: "Class billing setup", Icon: AccountBalanceOutlinedIcon, color: "warning" },
    { to: "/Admin/fees/collection", label: "Fee Collection", sub: "Record payments", Icon: PaymentsOutlinedIcon, color: "success" },
    { to: "/Admin/fees/defaulters", label: "View Defaulters", sub: "Overdue accounts", Icon: ReportOutlinedIcon, color: "warning" },
];

function KpiCard({ title, value, icon: Icon, color = "primary", prefix = "", suffix = "", statHint = "Live counts from your school data", to }) {
    return (
        <Card
            component={to ? RouterLink : "div"}
            to={to}
            elevation={0}
            sx={(t) => {
                const main = t.palette[color]?.main ?? t.palette.primary.main;
                const dark = t.palette[color]?.dark ?? main;
                return {
                    height: "100%",
                    borderRadius: 3,
                    overflow: "hidden",
                    border: `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    background: `linear-gradient(135deg, ${dark} 0%, ${main} 100%)`,
                    color: t.palette.common.white,
                    boxShadow: `0 8px 28px ${alpha(t.palette.common.black, 0.12)}`,
                    textDecoration: "none",
                    display: "block",
                    transition: t.transitions.create(["transform", "box-shadow"], { duration: 180 }),
                    ...(to
                        ? {
                              cursor: "pointer",
                              "&:hover": {
                                  transform: "translateY(-3px)",
                                  boxShadow: `0 12px 32px ${alpha(t.palette.common.black, 0.18)}`,
                              },
                          }
                        : {}),
                };
            }}
        >
            <CardContent sx={{ p: 2.5, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1 }}>
                            {title}
                        </Typography>
                        <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, lineHeight: 1.1 }}>
                            {prefix}
                            <CountUp end={value} duration={2.2} separator="," preserveValue />
                            {suffix || ""}
                        </Typography>
                    </Box>
                    <Avatar
                        sx={{
                            bgcolor: alpha("#fff", 0.2),
                            color: "inherit",
                            width: 48,
                            height: 48,
                        }}
                    >
                        <Icon />
                    </Avatar>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                    <Typography variant="caption" sx={{ opacity: 0.92 }}>
                        {statHint}
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
}

function QuickLinkCard({ to, label, sub, Icon, color = "primary" }) {
    return (
        <Card
            component={RouterLink}
            to={to}
            elevation={0}
            sx={(t) => {
                const main = t.palette[color]?.main ?? t.palette.primary.main;
                return {
                    textDecoration: "none",
                    color: "inherit",
                    height: "100%",
                    borderRadius: 3,
                    border: `1px solid ${alpha(t.palette.divider, 0.88)}`,
                    bgcolor: t.palette.background.paper,
                    position: "relative",
                    overflow: "hidden",
                    transition: t.transitions.create(["transform", "box-shadow", "border-color"], { duration: 200 }),
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${main}, ${alpha(main, 0.55)})`,
                    },
                    "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 14px 36px ${alpha(main, 0.22)}`,
                        borderColor: alpha(main, 0.4),
                        "& .quick-link-arrow": {
                            opacity: 1,
                            transform: "translateX(4px)",
                        },
                    },
                };
            }}
        >
            <CardContent sx={{ pt: 2.75, pb: 2, px: 2.25, height: "100%" }}>
                <Stack spacing={1.5} justifyContent="space-between" sx={{ height: "100%", minHeight: 108 }}>
                    <Avatar
                        variant="rounded"
                        sx={{
                            width: 52,
                            height: 52,
                            bgcolor: (t) => alpha(t.palette[color]?.main || t.palette.primary.main, 0.12),
                            color: (t) => t.palette[color]?.main || t.palette.primary.main,
                        }}
                    >
                        <Icon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.25 }}>
                            {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
                            {sub}
                        </Typography>
                    </Box>
                    <ArrowForwardOutlinedIcon
                        className="quick-link-arrow"
                        sx={{
                            alignSelf: "flex-end",
                            fontSize: 20,
                            color: `${color}.main`,
                            opacity: 0.35,
                            transition: (t) => t.transitions.create(["opacity", "transform"], { duration: 200 }),
                        }}
                    />
                </Stack>
            </CardContent>
        </Card>
    );
}

function ShortcutTile({ to, label, sub, Icon, color }) {
    return (
        <Card
            component={RouterLink}
            to={to}
            elevation={0}
            sx={{
                textDecoration: "none",
                color: "inherit",
                borderRadius: 2.5,
                border: (t) => `1px solid ${alpha(t.palette.divider, 0.85)}`,
                transition: (t) => t.transitions.create(["transform", "box-shadow", "border-color"], { duration: 180 }),
                "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: (t) => `0 10px 28px ${alpha(t.palette[color]?.main || t.palette.primary.main, 0.18)}`,
                    borderColor: (t) => alpha(t.palette[color]?.main || t.palette.primary.main, 0.45),
                },
            }}
        >
            <CardContent sx={{ py: 2, px: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                        variant="rounded"
                        sx={{
                            bgcolor: (t) => alpha(t.palette[color]?.main || t.palette.primary.main, 0.12),
                            color: (t) => t.palette[color]?.main || t.palette.primary.main,
                            width: 44,
                            height: 44,
                        }}
                    >
                        <Icon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                            {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {sub}
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}

const AdminHomePage = () => {
    const dispatch = useDispatch();
    const { studentsList } = useSelector((state) => state.student);
    const { sclassesList } = useSelector((state) => state.sclass);
    const { teachersList } = useSelector((state) => state.teacher);
    const { currentUser } = useSelector((state) => state.user);
    const { feeSummary, feeStructures, feePaymentSubmissions } = useSelector((state) => state.fee);
    const { canShowModule, filterLinks, filterChecklist } = useDashboardFeatures();

    const adminID = currentUser?._id;
    const showStudents = canShowModule("students");
    const showSetup = canShowModule("setup");
    const showFees = canShowModule("fees");
    const showDaily = canShowModule("daily");
    const showExams = canShowModule("exams");
    const showStart = canShowModule("start");
    const showPlanning = canShowModule("planning");
    const showHostels = canShowModule("hostels");

    const [recentActivity, setRecentActivity] = useState([]);
    const [activityLoading, setActivityLoading] = useState(true);
    const [activityError, setActivityError] = useState(null);
    const [termConfig, setTermConfig] = useState({
        primaryConfigured: false,
        secondaryConfigured: false,
    });
    const [hostelWithRooms, setHostelWithRooms] = useState(false);

    const offersBoarding = useMemo(
        () => schoolOffersBoarding(currentUser?.boardingMode, currentUser?.hasBoarding),
        [currentUser?.boardingMode, currentUser?.hasBoarding],
    );

    useEffect(() => {
        if (!adminID) return;
        if (showStudents) dispatch(getAllStudents(adminID));
        if (showSetup) {
            dispatch(getAllSclasses(adminID, "Sclass"));
        }
        if (showSetup || showDaily) {
            dispatch(getAllTeachers(adminID));
        }
        if (showFees) {
            dispatch(getFeeSummary(adminID, { recordsScope: "current" }));
            dispatch(getAllFeeStructures(adminID));
            dispatch(getPendingFeeSubmissions(adminID));
        }
    }, [adminID, dispatch, showStudents, showSetup, showDaily, showFees]);

    useEffect(() => {
        if (!adminID || !showFees) return;
        const refreshFinance = () => {
            dispatch(getFeeSummary(adminID, { recordsScope: "current" }));
            dispatch(getAllFeeStructures(adminID));
        };
        window.addEventListener("focus", refreshFinance);
        return () => window.removeEventListener("focus", refreshFinance);
    }, [adminID, dispatch, showFees]);

    useEffect(() => {
        if (!adminID || !showStart) return;
        let cancelled = false;

        const loadActivity = async () => {
            setActivityLoading(true);
            setActivityError(null);
            try {
                const { data } = await axios.get(`${getApiBaseUrl()}/RecentActivity/${adminID}`, {
                    params: { limit: 12 },
                });
                if (!cancelled) {
                    setRecentActivity(Array.isArray(data?.activities) ? data.activities : []);
                }
            } catch (err) {
                if (!cancelled) {
                    setRecentActivity([]);
                    setActivityError(err.response?.data?.message || err.message || "Could not load activity");
                }
            } finally {
                if (!cancelled) setActivityLoading(false);
            }
        };

        loadActivity();
        return () => {
            cancelled = true;
        };
    }, [adminID, showStart]);

    const hasPrimaryClasses = useMemo(() => {
        const list = Array.isArray(sclassesList) ? sclassesList : [];
        return list.some((c) => inferClassEducationLevel(c.sclassName) === "Primary");
    }, [sclassesList]);

    const hasSecondaryClasses = useMemo(() => {
        const list = Array.isArray(sclassesList) ? sclassesList : [];
        return list.some((c) => inferClassEducationLevel(c.sclassName) === "Secondary");
    }, [sclassesList]);

    const refreshSetupStatus = useCallback(() => {
        if (!adminID) return;

        fetchTermConfigurationStatus({
            schoolId: adminID,
            schoolType: currentUser?.schoolType,
            hasPrimaryClasses,
            hasSecondaryClasses,
        })
            .then((status) => setTermConfig(status))
            .catch(() => {
                setTermConfig({ primaryConfigured: false, secondaryConfigured: false });
            });

        if (!offersBoarding || !showHostels) {
            setHostelWithRooms(false);
            return;
        }

        axios
            .get(`${getApiBaseUrl()}/Hostels/${adminID}`)
            .then(({ data }) => {
                const list = Array.isArray(data) ? data : [];
                const ready = list.some(
                    (h) =>
                        (Array.isArray(h.rooms) && h.rooms.length > 0) ||
                        Number(h.roomCount) > 0,
                );
                setHostelWithRooms(ready);
            })
            .catch(() => setHostelWithRooms(false));
    }, [adminID, currentUser?.schoolType, offersBoarding, showHostels, hasPrimaryClasses, hasSecondaryClasses]);

    useEffect(() => {
        if (showSetup || showPlanning || showHostels || showFees || showStudents) {
            refreshSetupStatus();
        }
    }, [refreshSetupStatus, showSetup, showPlanning, showHostels, showFees, showStudents]);

    useEffect(() => {
        if (!adminID || !showSetup) return;
        const refreshOnReturn = () => {
            dispatch(getAllSclasses(adminID, "Sclass"));
            refreshSetupStatus();
        };
        const onVisible = () => {
            if (document.visibilityState === "visible") refreshOnReturn();
        };
        window.addEventListener("focus", refreshOnReturn);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.removeEventListener("focus", refreshOnReturn);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [adminID, dispatch, refreshSetupStatus, showSetup]);

    const numberOfStudents = useMemo(
        () => (Array.isArray(studentsList) ? studentsList.length : 0),
        [studentsList]
    );
    const numberOfClasses = useMemo(() => (Array.isArray(sclassesList) ? sclassesList.length : 0), [sclassesList]);
    const allHomeroomsAssigned = useMemo(
        () => allClassesHaveHomeroom(sclassesList),
        [sclassesList],
    );
    const numberOfTeachers = useMemo(() => (Array.isArray(teachersList) ? teachersList.length : 0), [teachersList]);

    const activeFeeStructures = useMemo(() => {
        const list = Array.isArray(feeStructures) ? feeStructures : [];
        return list.filter((row) => row.isActive !== false);
    }, [feeStructures]);

    const numberOfFeeStructures = activeFeeStructures.length;

    const pendingPaymentProofCount = useMemo(() => {
        const list = Array.isArray(feePaymentSubmissions) ? feePaymentSubmissions : [];
        return list.filter((row) => row.status === "Pending").length;
    }, [feePaymentSubmissions]);

    const setupChecklist = useMemo(
        () =>
            filterChecklist(
                getSetupChecklist({
                    classCount: numberOfClasses,
                    teacherCount: numberOfTeachers,
                    studentCount: numberOfStudents,
                    feeStructureCount: numberOfFeeStructures,
                    primaryTermConfigured: termConfig.primaryConfigured,
                    secondaryTermConfigured: termConfig.secondaryConfigured,
                    schoolType: currentUser?.schoolType,
                    offersBoarding,
                    hostelWithRooms,
                    hasPrimaryClasses,
                    hasSecondaryClasses,
                    allHomeroomsAssigned,
                }),
            ),
        [
            numberOfClasses,
            numberOfTeachers,
            numberOfStudents,
            numberOfFeeStructures,
            termConfig.primaryConfigured,
            termConfig.secondaryConfigured,
            currentUser?.schoolType,
            offersBoarding,
            hostelWithRooms,
            hasPrimaryClasses,
            hasSecondaryClasses,
            allHomeroomsAssigned,
            filterChecklist,
        ],
    );

    const feeStructuresPreview = useMemo(() => activeFeeStructures.slice(0, 6), [activeFeeStructures]);

    const heroActions = useMemo(() => filterLinks(HERO_ACTIONS), [filterLinks]);
    const quickLinks = useMemo(() => filterLinks(QUICK_LINKS), [filterLinks]);
    const shortcuts = useMemo(() => filterLinks(SHORTCUTS), [filterLinks]);

    const kpiCards = useMemo(() => {
        const cards = [];
        if (showStudents) {
            cards.push({
                key: "students",
                title: "Total students",
                value: numberOfStudents,
                icon: PeopleAltOutlinedIcon,
                color: "primary",
            });
        }
        if (showSetup) {
            cards.push({
                key: "classes",
                title: "Classes",
                value: numberOfClasses,
                icon: ClassOutlinedIcon,
                color: "secondary",
            });
            cards.push({
                key: "teachers",
                title: "Teachers",
                value: numberOfTeachers,
                icon: SupervisorAccountOutlinedIcon,
                color: "info",
            });
        }
        if (showFees) {
            cards.push({
                key: "fee-structures",
                title: "Fee structures",
                value: numberOfFeeStructures,
                icon: AccountBalanceOutlinedIcon,
                color: "warning",
                to: "/Admin/fees/structures",
                statHint:
                    numberOfFeeStructures > 0
                        ? "Active class fee setups — auto-applied to new students"
                        : "Create structures so every new student is billed",
            });
            cards.push({
                key: "fee-expected",
                title: "Expected (term)",
                value: feeSummary ? Number(feeSummary.totalExpected) : 0,
                icon: AccountBalanceOutlinedIcon,
                color: "warning",
                prefix: "MWK ",
                to: "/Admin/fees/reports",
                statHint: feeSummary
                    ? `${feeSummary.recordCount ?? 0} student fee record(s) billed`
                    : "Loading fee data...",
            });
            cards.push({
                key: "fee-collected",
                title: "Fee collected (term)",
                value: feeSummary ? Number(feeSummary.totalCollected) : 0,
                icon: PaymentsOutlinedIcon,
                color: "success",
                prefix: "MWK ",
                to: "/Admin/fees/collection",
                statHint: feeSummary
                    ? `${feeSummary.collectionRate} collected · MWK ${Number(feeSummary.totalOutstanding || 0).toLocaleString()} still owed`
                    : "Loading fee data...",
            });
        }
        return cards;
    }, [
        showStudents,
        showSetup,
        showFees,
        numberOfStudents,
        numberOfClasses,
        numberOfTeachers,
        numberOfFeeStructures,
        feeSummary,
    ]);

    const schoolLabel = currentUser?.schoolName || "School";
    const schoolType = currentUser?.schoolType || "Secondary";
    const schoolTypeLabel = useMemo(() => {
        if (schoolType === "Primary") return "Primary School";
        if (schoolType === "Secondary") return "Secondary School";
        if (schoolType === "University") return "University";
        if (schoolType === "Combined") return "Combined School";
        return `${schoolType} School`;
    }, [schoolType]);
    const schoolTypeSummary = useMemo(() => {
        if (schoolType === "Primary") return "Standards 1-8 · PSLCE exam at Std 8";
        if (schoolType === "Secondary") return "Forms 1-4 · JCE at Form 2 · MSCE at Form 4";
        if (schoolType === "University") return "Semester-based · GPA 4.0 scale";
        if (schoolType === "Combined") return "Primary Std 1-8 + Secondary Forms 1-4";
        return "Multi-level school structure";
    }, [schoolType]);

    return (
        <Box
            sx={{
                minHeight: "100%",
                pb: 4,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.06)} 0%, ${t.palette.grey[100]} 22%)`
                        : alpha(t.palette.common.black, 0.2),
            }}
        >
            <Container maxWidth="xl" sx={{ pt: { xs: 2, sm: 3 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, sm: 3.5 },
                        mb: 3,
                        borderRadius: 3,
                        color: "common.white",
                        background: (t) =>
                            `linear-gradient(115deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 48%, ${alpha(
                                t.palette.secondary.main,
                                0.88
                            )} 100%)`,
                        boxShadow: (t) => `0 16px 48px ${alpha(t.palette.primary.main, 0.28)}`,
                    }}
                >
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                                sx={{
                                    width: 56,
                                    height: 56,
                                    bgcolor: alpha("#fff", 0.18),
                                    color: "common.white",
                                }}
                            >
                                <SchoolOutlinedIcon fontSize="large" />
                            </Avatar>
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.2 }}>
                                    School office — start here
                                </Typography>
                                <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                                    {schoolLabel}
                                </Typography>
                                {showPlanning ? <CurrentTermIndicator variant="hero" /> : null}
                                <Typography variant="body2" sx={{ opacity: 0.92, maxWidth: 560, mt: 0.5 }}>
                                    {showStart
                                        ? "This screen replaces the head teacher’s daily diary. Use the menu on the left in order: Step 1 setup, then students, daily work, exams, and fees."
                                        : "Admin Only mode — showing widgets for your selected modules only."}
                                </Typography>
                            </Box>
                        </Stack>
                        {heroActions.length > 0 ? (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {heroActions.map((action) => (
                                    <Button
                                        key={action.to}
                                        variant={action.variant}
                                        component={RouterLink}
                                        to={action.to}
                                        sx={
                                            action.variant === "contained"
                                                ? {
                                                      bgcolor: "common.white",
                                                      color: "primary.main",
                                                      fontWeight: 700,
                                                      "&:hover": { bgcolor: alpha("#fff", 0.92) },
                                                  }
                                                : {
                                                      borderColor: alpha("#fff", 0.65),
                                                      color: "common.white",
                                                      fontWeight: 600,
                                                  }
                                        }
                                    >
                                        {action.label}
                                    </Button>
                                ))}
                            </Stack>
                        ) : null}
                    </Stack>
                </Paper>

                {setupChecklist.length > 0 ? <AdminSetupChecklist items={setupChecklist} /> : null}

                <SubscriptionStatusBanner user={currentUser} />

                {showFees ? (
                    <PendingFeePaymentAlert count={pendingPaymentProofCount} verifyPath="/Admin/fees/payment-submissions" />
                ) : null}

                {showFees && feeSummary != null && Number(feeSummary.totalOutstanding) > 0 && (
                    <Alert severity="warning" variant="filled" sx={{ mb: 3, borderRadius: 2 }} icon={<PaymentsOutlinedIcon fontSize="inherit" />}>
                        <AlertTitle>Fees still owed</AlertTitle>
                        About{" "}
                        <strong>MWK {Number(feeSummary.totalOutstanding).toLocaleString()}</strong> is still owed by some families.
                        Open Step 5 → Who owes fees, then record payments in Collect fees when money is received.
                        <Box sx={{ mt: 1.5 }}>
                            <Button
                                color="inherit"
                                variant="outlined"
                                size="small"
                                component={RouterLink}
                                to="/Admin/fees/defaulters"
                                sx={{ borderColor: alpha("#000", 0.25), color: "inherit" }}
                            >
                                View defaulters
                            </Button>
                        </Box>
                    </Alert>
                )}

                {kpiCards.length > 0 ? (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                                md: "repeat(3, 1fr)",
                                lg: `repeat(${Math.min(6, kpiCards.length)}, 1fr)`,
                            },
                            gap: 2.5,
                            mb: 3,
                        }}
                    >
                        {kpiCards.map((card) => (
                            <KpiCard key={card.key} {...card} />
                        ))}
                    </Box>
                ) : null}

                {showDaily ? (
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, sm: 2.5 },
                            mb: 3,
                            borderRadius: 3,
                            border: (t) => `1px solid ${alpha(t.palette.secondary.main, 0.28)}`,
                            bgcolor: (t) => alpha(t.palette.secondary.main, 0.04),
                        }}
                    >
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={2}
                            justifyContent="space-between"
                            alignItems={{ md: "center" }}
                        >
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar
                                    sx={{
                                        width: 52,
                                        height: 52,
                                        bgcolor: (t) => alpha(t.palette.secondary.main, 0.14),
                                        color: "secondary.main",
                                    }}
                                >
                                    <AccessTimeOutlinedIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="overline" color="text.secondary">
                                        School office — today
                                    </Typography>
                                    <Typography variant="h6" fontWeight={800}>
                                        Staff sign-in book
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Record arrival and departure for all staff on the selected day.
                                    </Typography>
                                    <Chip
                                        size="small"
                                        icon={<SupervisorAccountOutlinedIcon />}
                                        label={`${numberOfTeachers} staff on register`}
                                        sx={{ mt: 1 }}
                                        variant="outlined"
                                    />
                                </Box>
                            </Stack>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                    variant="outlined"
                                    component={RouterLink}
                                    to="/Admin/staff/attendance?add=1"
                                    startIcon={<PersonAddAlt1OutlinedIcon />}
                                    sx={{ borderRadius: 2 }}
                                >
                                    Add names
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    component={RouterLink}
                                    to="/Admin/staff/attendance"
                                    startIcon={<AccessTimeOutlinedIcon />}
                                    sx={{ borderRadius: 2, fontWeight: 700 }}
                                >
                                    Open sign-in book
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                ) : null}

                {showFees ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        mb: 3,
                        borderRadius: 3,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1.5}
                        sx={{ mb: 2 }}
                    >
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                Fee structures
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {schoolType === "Secondary" || schoolType === "Combined"
                                    ? "Tuition is applied to every secondary class automatically; other fees follow the class you set. New students receive active structures on registration."
                                    : "Each active structure is assigned to all students in that class when they are registered."}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button
                                variant="outlined"
                                component={RouterLink}
                                to="/Admin/fees/structures"
                                size="small"
                                sx={{ borderRadius: 2 }}
                            >
                                Manage all
                            </Button>
                            <Button
                                variant="contained"
                                component={RouterLink}
                                to="/Admin/fees/structures"
                                size="small"
                                startIcon={<AddIcon />}
                                sx={{ borderRadius: 2, fontWeight: 700 }}
                            >
                                New structure
                            </Button>
                        </Stack>
                    </Stack>

                    {numberOfFeeStructures === 0 ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            No fee structures yet. Add one per class and term so new students automatically receive the correct fee
                            records.
                        </Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Class</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Term</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Academic year</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            Students
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            Expected (MWK)
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            Collected (MWK)
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {feeStructuresPreview.map((row) => (
                                        <TableRow key={row._id || row.id} hover>
                                            <TableCell>{row.sclass?.sclassName ?? "—"}</TableCell>
                                            <TableCell>{row.term}</TableCell>
                                            <TableCell>{row.academicYear}</TableCell>
                                            <TableCell align="right" fontWeight={600}>
                                                {Number(row.billedStudentCount ?? 0).toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right" fontWeight={600}>
                                                {Number(row.totalExpected ?? 0).toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right" fontWeight={600} sx={{ color: "success.main" }}>
                                                {Number(row.totalCollected ?? 0).toLocaleString()}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.isActive === false ? "Inactive" : "Active"}
                                                    color={row.isActive === false ? "default" : "success"}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {numberOfFeeStructures > feeStructuresPreview.length ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                            Showing {feeStructuresPreview.length} of {numberOfFeeStructures} structures.
                        </Typography>
                    ) : null}
                </Paper>
                ) : null}

                {(showSetup || showStart) ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 3,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                            sx={{
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                                color: "primary.main",
                                width: 44,
                                height: 44,
                            }}
                        >
                            <SchoolOutlinedIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                                Education Level
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {schoolTypeLabel}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {schoolTypeSummary}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Residence: {boardingModeLabel(currentUser?.boardingMode, currentUser?.hasBoarding)}
                                {showHostels && schoolOffersBoarding(currentUser?.boardingMode, currentUser?.hasBoarding)
                                    ? " · Hostels module enabled"
                                    : ""}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
                ) : null}

                {quickLinks.length > 0 ? (
                    <>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                            Quick Links
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Frequent tasks — jump straight to enrolment, fees, and results
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(2, 1fr)",
                                    md: "repeat(3, 1fr)",
                                    lg: `repeat(${Math.min(5, quickLinks.length)}, 1fr)`,
                                },
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            {quickLinks.map((link) => (
                                <QuickLinkCard key={link.to} {...link} />
                            ))}
                        </Box>
                    </>
                ) : null}

                {shortcuts.length > 0 ? (
                    <>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                            Shortcuts
                        </Typography>
                        <Grid container spacing={1.5} sx={{ mb: 3 }}>
                            {shortcuts.map((s) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={s.to}>
                                    <ShortcutTile {...s} />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                ) : null}

                {(showDaily || showStart) && (
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    {showDaily ? (
                    <Grid item xs={12} lg={showStart ? 8 : 12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: 3,
                                height: 360,
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>
                                        Attendance overview
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Average daily presence rate (%) — sample trend for the current week
                                    </Typography>
                                </Box>
                                <Chip size="small" label="This week" color="primary" variant="outlined" />
                            </Stack>
                            <Divider sx={{ mb: 1 }} />
                            <Box sx={{ width: "100%", height: 260 }}>
                                <ResponsiveContainer>
                                    <ComposedChart data={ATTENDANCE_OVERVIEW} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#1976d2" stopOpacity={0.35} />
                                                <stop offset="95%" stopColor="#1976d2" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.35} />
                                        <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                                        <YAxis domain={[70, 100]} tick={{ fontSize: 12 }} unit="%" />
                                        <Tooltip formatter={(v) => [`${v}%`, ""]} />
                                        <Legend />
                                        <Area
                                            type="monotone"
                                            dataKey="present"
                                            name="Present"
                                            stroke="#1565c0"
                                            fillOpacity={1}
                                            fill="url(#colorPresent)"
                                            strokeWidth={2}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="absent"
                                            name="Absent %"
                                            stroke="#ef6c00"
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>
                    </Grid>
                    ) : null}
                    {showStart ? (
                    <Grid item xs={12} lg={showDaily ? 4 : 12}>
                        <RecentActivityPanel
                            activities={recentActivity}
                            loading={activityLoading}
                            error={activityError}
                        />
                    </Grid>
                    ) : null}
                </Grid>
                )}

                {(showStart || showExams) && (
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    {showStart ? (
                    <Grid item xs={12} md={showExams ? 6 : 12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: 3,
                                height: 340,
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                            }}
                        >
                            <Typography variant="h6" fontWeight={700}>
                                Performance index
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Composite score from attendance, submissions, and behaviour (demo)
                            </Typography>
                            <Box sx={{ width: "100%", height: 240 }}>
                                <ResponsiveContainer>
                                    <LineChart data={PERFORMANCE_TREND} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.35} />
                                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                        <YAxis domain={[60, 100]} tick={{ fontSize: 12 }} />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="score" name="Index" stroke="#7b1fa2" strokeWidth={3} dot={{ r: 4 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>
                    </Grid>
                    ) : null}
                    {showExams ? (
                    <Grid item xs={12} md={showStart ? 6 : 12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: 3,
                                height: 340,
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                            }}
                        >
                            <Typography variant="h6" fontWeight={700}>
                                Exam results summary
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Continuous assessment averages and pass rates by subject (demo)
                            </Typography>
                            <Box sx={{ width: "100%", height: 240 }}>
                                <ResponsiveContainer>
                                    <BarChart data={EXAM_RESULTS_SUMMARY} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} strokeOpacity={0.35} />
                                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                                        <YAxis type="category" dataKey="subject" width={110} tick={{ fontSize: 11 }} />
                                        <Tooltip
                                            formatter={(value, name) =>
                                                name === "avg" ? [`${value}%`, "Class avg"] : [`${value}%`, "Pass rate"]
                                            }
                                        />
                                        <Legend />
                                        <Bar dataKey="avg" name="Class avg" radius={[0, 6, 6, 0]} fill="#00897b">
                                            {EXAM_RESULTS_SUMMARY.map((_, i) => (
                                                <Cell key={i} fill={i % 2 === 0 ? "#00897b" : "#26a69a"} />
                                            ))}
                                        </Bar>
                                        <Bar dataKey="passRate" name="Pass rate" radius={[0, 6, 6, 0]} fill="#5c6bc0" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                            <Stack spacing={1} sx={{ mt: 1 }}>
                                {EXAM_RESULTS_SUMMARY.slice(0, 2).map((row) => (
                                    <Box key={row.subject}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="caption" color="text.secondary">
                                                {row.subject}
                                            </Typography>
                                            <Typography variant="caption" fontWeight={700}>
                                                Avg {row.avg}% · Pass {row.passRate}%
                                            </Typography>
                                        </Stack>
                                        <LinearProgress variant="determinate" value={row.avg} sx={{ height: 6, borderRadius: 3 }} />
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                    ) : null}
                </Grid>
                )}

                {showDaily ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    }}
                >
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                        Notices
                    </Typography>
                    <SeeNotice />
                </Paper>
                ) : null}
            </Container>
        </Box>
    );
};

export default AdminHomePage;
